/**
 * Microsoft Clarity Analytics Integration
 *
 * Initializes and manages Microsoft Clarity tracking for user behavior analytics.
 * @see https://clarity.microsoft.com/
 */

/**
 * Initializes Microsoft Clarity tracking script
 * @returns {void}
 */
export const initClarity = () => {
  const clarityId = process.env.REACT_APP_CLARITY_ID;

  // Skip initialization if:
  // - No Clarity ID is configured
  // - Clarity is already initialized
  // - Running in development mode (optional - remove this check if you want tracking in dev)
  if (!clarityId || typeof window.clarity === 'function') {
    return;
  }

  // Initialize Clarity queue
  window.clarity = window.clarity || function() {
    (window.clarity.q = window.clarity.q || []).push(arguments);
  };

  // Load Clarity script asynchronously
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${clarityId}`;

  // Add error handling
  script.onerror = () => {
    console.warn('Failed to load Microsoft Clarity script');
  };

  document.head.appendChild(script);
};

/**
 * Identify a user in Clarity (optional)
 * @param {string} userId - Unique user identifier
 * @param {string} sessionId - Session identifier (optional)
 * @param {string} pageId - Page identifier (optional)
 */
export const identifyClarityUser = (userId, sessionId, pageId) => {
  if (typeof window.clarity === 'function') {
    window.clarity('identify', userId, sessionId, pageId);
  }
};

/**
 * Set custom tags in Clarity (optional)
 * @param {string} key - Tag key
 * @param {string} value - Tag value
 */
export const setClarityTag = (key, value) => {
  if (typeof window.clarity === 'function') {
    window.clarity('set', key, value);
  }
};

/**
 * Upgrade Clarity session (optional - for enhanced features)
 * @param {string} reason - Reason for upgrading the session
 */
export const upgradeClaritySession = (reason) => {
  if (typeof window.clarity === 'function') {
    window.clarity('upgrade', reason);
  }
};
